"use client";

import { ThemeProvider } from "@mui/material/styles";
import { usePathname } from "next/navigation";
import { NextIntlClientProvider } from "next-intl";
import { SnackbarProvider } from "notistack";
import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import store from "../store";
import { theme } from "../theme";
import { initializeAppInsights } from "../utils/appInsights";
import { initClarity, updateClarityConsent } from "../utils/clarity";
import { allowLanguages } from "../utils/consent";
import AppRouteTracker from "./AppRouteTracker";
import CookieBanner from "./CookieBanner";
const ClientComponentWithLocale = ({
  children
}) => {
  const [locale, setLocale] = useState("en");
  const pathname = usePathname();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [clarityReady, setClarityReady] = useState(false);
  const loadLocaleFromLocalStorage = () => {
    const userLanguage = typeof navigator !== "undefined" ? navigator.language : "en";
    const detectedLocale = userLanguage.split("-")[0].toLowerCase();
    const primaryLocale = allowLanguages.includes(detectedLocale) ? detectedLocale : "en";
    const storedLocale = localStorage.getItem("language") || primaryLocale;

    // Validate that the locale is supported, fallback to 'en' if not
    const validLocale = allowLanguages.includes(storedLocale) ? storedLocale : "en";

    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🌐 Locale Detection:', {
        userLanguage,
        detectedLocale,
        primaryLocale,
        storedLocale,
        validLocale,
        allowLanguages
      });
    }
    setLocale(validLocale);
  };
  useEffect(() => {
    const params = new URLSearchParams(window?.location?.search);
    const lang = params.get("lang") || null;
    if (lang && allowLanguages.includes(lang)) {
      localStorage.setItem("language", lang);
      setLocale(lang);
    } else {
      loadLocaleFromLocalStorage();
    }
    const handleStorageChange = () => {
      loadLocaleFromLocalStorage();
      // Re-check analytics when storage changes (cross-tab sync)
      initializeAnalytics();
    };

    // Core function to manage Clarity based on login state and consent
    const initializeAnalytics = () => {
      // Check if user is logged in (has valid token)
      const userToken = localStorage.getItem("token");
      const loggedIn = !!userToken;
      setIsLoggedIn(loggedIn);

      // Get saved cookie preferences for marketing/ads
      const savedPreferences = localStorage.getItem("descripio-cookie-preferences");
      let marketingAllowed = false;
      let analyticsAllowed = false;
      if (savedPreferences) {
        try {
          const preferences = JSON.parse(savedPreferences);
          marketingAllowed = preferences.marketing || false;
          analyticsAllowed = preferences.analytics || false;
        } catch (e) {
          console.error("Failed to parse saved cookie preferences:", e);
        }
      }
      if (loggedIn) {
        // Logged-in users: Always initialize Clarity (T&C consent)
        // But still respect their marketing preference
        initClarity();
        setClarityReady(true);
        setTimeout(() => {
          updateClarityConsent({
            analytics: true,
            // Always true for logged-in users (T&C)
            ads: marketingAllowed // Respect saved marketing preference
          });
        }, 0);
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Clarity: Active for logged-in user (T&C consent)', {
            marketing: marketingAllowed
          });
        }
      } else if (analyticsAllowed) {
        // Non-logged-in users with analytics consent
        initClarity();
        setClarityReady(true);
        setTimeout(() => {
          updateClarityConsent({
            analytics: true,
            ads: marketingAllowed
          });
        }, 0);
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Clarity: Active with user consent');
        }
      } else {
        // User is NOT logged in AND has no/rejected analytics consent
        // Send denied signal (important for logout scenarios)
        setTimeout(() => {
          updateClarityConsent({
            analytics: false,
            ads: false
          });
        }, 0);
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Clarity: Inactive - no consent/not logged in');
        }
      }

      // Initialize Application Insights (this is for error tracking, separate from Clarity)
      initializeAppInsights();
    };

    // Load analytics after page load
    if (document.readyState === 'complete') {
      initializeAnalytics();
    } else {
      window.addEventListener('load', initializeAnalytics);
    }
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener('load', initializeAnalytics);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [pathname]); // Re-run on pathname changes to catch login/logout

  let messages;
  try {
    // Load consolidated translation file, listing generator file, dashboard file, api-keys file and tour file
    const mainTranslations = require(`../../message/${locale}/${locale}.json`);
    const listingGenerator = require(`../../message/${locale}/${locale}_listing-generator.json`);
    const dashboard = require(`../../message/${locale}/${locale}_dashboard.json`);
    const apiKeys = require(`../../message/${locale}/${locale}_api-keys.json`);
    let tour = {};
    try {
      tour = require(`../../message/${locale}/${locale}_tour.json`);
    } catch (tourError) {
      console.error(`Error loading tour messages for locale '${locale}':`, tourError);
    }

    // Combine all messages (later spreads override earlier ones on the same top-level key)
    messages = {
      ...mainTranslations,
      ...listingGenerator,
      ...dashboard,
      ...apiKeys,
      ...tour
    };
  } catch (error) {
    console.error(`Error loading messages for locale '${locale}':`, error);
    // Fallback to English
    try {
      const mainTranslations = require("../../message/en/en.json");
      const listingGenerator = require("../../message/en/en_listing-generator.json");
      const dashboard = require("../../message/en/en_dashboard.json");
      const apiKeys = require("../../message/en/en_api-keys.json");
      let tour = {};
      try {
        tour = require("../../message/en/en_tour.json");
      } catch (tourError) {
        console.error("Error loading tour messages for fallback locale 'en':", tourError);
      }
      messages = {
        ...mainTranslations,
        ...listingGenerator,
        ...dashboard,
        ...apiKeys,
        ...tour
      };
    } catch (fallbackError) {
      console.error("Failed to load fallback English messages:", fallbackError);
      messages = {}; // Empty fallback
    }
  }

  // Handler for cookie consent changes (called when user interacts with CookieBanner)
  const handleConsentChange = ({
    analytics,
    marketing
  }) => {
    // Check if user is logged in
    const userToken = localStorage.getItem("token");
    const loggedIn = !!userToken;
    if (loggedIn) {
      // Logged-in users: Clarity is always active per T&C
      // We only respect their marketing (ads) preference
      initClarity();
      setClarityReady(true);
      setTimeout(() => {
        updateClarityConsent({
          analytics: true,
          // Always true for logged-in users
          ads: marketing
        });
      }, 0);
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Clarity: Logged-in user - always active (T&C)', {
          marketing
        });
      }
    } else if (analytics) {
      // Non-logged-in user accepted analytics
      initClarity();
      setClarityReady(true);
      setTimeout(() => {
        updateClarityConsent({
          analytics: true,
          ads: marketing
        });
      }, 0);
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Clarity: User accepted cookies - initialized');
      }
    } else {
      // Non-logged-in user rejected analytics
      setTimeout(() => {
        updateClarityConsent({
          analytics: false,
          ads: false
        });
      }, 0);
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Clarity: User rejected cookies - not recording');
      }
    }

    // You can add other analytics integrations here
    // For example, Google Analytics, Facebook Pixel, etc.
  };
  return <NextIntlClientProvider locale={locale} messages={messages} data-sentry-element="NextIntlClientProvider" data-sentry-component="ClientComponentWithLocale" data-sentry-source-file="clientComponent.jsx">
      <ThemeProvider theme={theme} data-sentry-element="ThemeProvider" data-sentry-source-file="clientComponent.jsx">
        <Provider store={store} data-sentry-element="Provider" data-sentry-source-file="clientComponent.jsx">
            <SnackbarProvider maxSnack={3} anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }} data-sentry-element="SnackbarProvider" data-sentry-source-file="clientComponent.jsx">
              <AppRouteTracker clarityReady={clarityReady} data-sentry-element="AppRouteTracker" data-sentry-source-file="clientComponent.jsx" />
              {children}
              <CookieBanner onConsentChange={handleConsentChange} isLoggedIn={isLoggedIn} data-sentry-element="CookieBanner" data-sentry-source-file="clientComponent.jsx" />
            </SnackbarProvider>
        </Provider>
      </ThemeProvider>
    </NextIntlClientProvider>;
};
export default ClientComponentWithLocale;